import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { ReportDataset, ReportInput, ReportSchedule, RequestExportInput } from '@/api/reports'
import { useApiClient } from '@/api/ApiClientProvider'
import type { ApiClient } from '@/api/client'
import { filtersKey, useGlobalFilters } from '@/lib/useGlobalFilters'

/** A Reports query with no dataset rows to filter — the saved-report list and the exports list. */
export function useReportsQuery<T>(name: string, fetch: (client: ApiClient) => Promise<T>) {
  const client = useApiClient()
  return useQuery({ queryKey: ['reports', name, client.kind], queryFn: () => fetch(client) })
}

/** One saved report by id. */
export function useSavedReport(id: string) {
  const client = useApiClient()
  return useQuery({ queryKey: ['reports', 'report', id, client.kind], queryFn: () => client.savedReport(id) })
}

/** A report's rows for the current global filters, like every other filtered dataset in the app. Disabled until the dataset is known (the report is still loading). */
export function useReportRows(dataset: ReportDataset | undefined) {
  const client = useApiClient()
  const { filters } = useGlobalFilters()
  return useQuery({
    queryKey: ['reports', 'rows', dataset, client.kind, filtersKey(filters)],
    queryFn: () => client.reportRows(dataset as ReportDataset, filters),
    enabled: dataset !== undefined,
    placeholderData: keepPreviousData,
  })
}

function useInvalidateReports() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ['reports'] })
}

export function useCreateSavedReport() {
  const client = useApiClient()
  const invalidate = useInvalidateReports()
  return useMutation({
    mutationFn: (input: ReportInput) => client.createSavedReport(input),
    onSuccess: () => invalidate(),
  })
}

export function useDuplicateSavedReport() {
  const client = useApiClient()
  const invalidate = useInvalidateReports()
  return useMutation({
    mutationFn: (id: string) => client.duplicateSavedReport(id),
    onSuccess: () => invalidate(),
  })
}

export function useScheduleSavedReport() {
  const client = useApiClient()
  const invalidate = useInvalidateReports()
  return useMutation({
    mutationFn: ({ id, schedule }: { id: string; schedule: ReportSchedule | null }) => client.scheduleSavedReport(id, schedule),
    onSuccess: () => invalidate(),
  })
}

export function useRequestExport() {
  const client = useApiClient()
  const invalidate = useInvalidateReports()
  return useMutation({
    mutationFn: (input: RequestExportInput) => client.requestExport(input),
    onSuccess: () => invalidate(),
  })
}
